import React, { useState, useRef } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Link,
  useColorModeValue,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Input,
  InputGroup,
  InputRightElement,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FaInstagram, FaFacebook } from 'react-icons/fa';
import logo from '../../assets/logo.svg';

const Navigation = () => {
  const { isOpen, onOpen, onClose } = useDisclosure(); // Desktop registry finder
  const { 
    isOpen: isMobilePopoverOpen, 
    onOpen: onMobilePopoverOpen, 
    onClose: onMobilePopoverClose 
  } = useDisclosure(); // Mobile registry finder
  
  const [registryId, setRegistryId] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const mobileMenuRef = useRef<HTMLButtonElement>(null);

  // Function to get current page name based on pathname
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'Home';
    if (path === '/how-it-works') return 'How It Works';
    if (path === '/faq') return 'FAQ';
    if (path === '/register') return 'Create';
    if (path.startsWith('/registry/')) return 'Registry';
    if (path === '/find-a-friend') return 'Find';
    if (path === '/login') return 'Login';
    if (path === '/support') return 'Contact Us';
    if (path === '/company-access') return 'Company Access';
    return 'Menu'; // fallback
  };

  const handleViewRegistry = () => {
    if (registryId.trim()) {
      navigate(`/registry/${registryId.trim()}`);
      setRegistryId('');
      onClose();
      onMobilePopoverClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleViewRegistry();
    }
  };

  const handleSocialClick = (platform: string) => {
    if (platform === 'facebook') {
      window.open('https://www.facebook.com/people/MommaFuel/61577361631105/', '_blank');
    } else if (platform === 'instagram') {
      window.open('https://www.instagram.com/mommafuel_/', '_blank');
    }
  };

  return (
    <Box position="relative" width="100%">
      <Flex
        bg="mamaFuel.cream"
        minH="60px"
        py={{ base: "2" }}
        px={{ base: "4" }}
        borderBottom="1px"
        borderStyle="solid"
        borderColor="mamaFuel.moss.500"
        align="center"
        position="fixed"
        top="0"
        left="0"
        right="0"
        width="100%"
        zIndex="1001"
        boxShadow="0 2px 4px rgba(0,0,0,0.05)"
      >
        <Flex flex={{ base: 1 }} justify={{ base: 'start', lg: 'start' }}>
          <Link
            as={RouterLink}
            to="/"
            textAlign="left"
          >
            <HStack spacing="2" align="center">
              <img src={logo} width="50px" alt="MommaFuel" />
              <Text
                fontFamily="heading"
                color="mamaFuel.moss.700"
                fontWeight="bold"
                fontSize="xl"
              >
                MommaFuel
              </Text>
            </HStack>
          </Link>

          {/* Desktop Navigation */}
          <Flex display={{ base: 'none', lg: 'flex' }} ml="10">
            <Stack direction="row" spacing="4" align="center">
              <Link 
                as={RouterLink} 
                to="/" 
                variant="nav"
                bg={location.pathname === '/' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                Home
              </Link>
              <Link 
                as={RouterLink} 
                to="/how-it-works" 
                variant="nav"
                bg={location.pathname === '/how-it-works' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/how-it-works' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                How It Works
              </Link>
              <Link 
                as={RouterLink} 
                to="/faq" 
                variant="nav"
                bg={location.pathname === '/faq' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/faq' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                FAQ
              </Link>
              <Link 
                as={RouterLink} 
                to="/find-a-friend" 
                variant="nav"
                bg={location.pathname === '/find-a-friend' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/find-a-friend' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                Find a Friend's MommaFuel
              </Link>
              <Link 
                as={RouterLink} 
                to="/login" 
                variant="nav"
                bg={location.pathname === '/login' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/login' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                Login to My MommaFuel
              </Link>
              <Link 
                as={RouterLink} 
                to="/support" 
                variant="nav"
                bg={location.pathname === '/support' ? 'mamaFuel.moss.100' : 'transparent'}
                border="2px solid"
                borderColor={location.pathname === '/support' ? 'mamaFuel.moss.500' : 'transparent'}
                borderRadius="md"
                px="3"
                py="2"
                _hover={{
                  bg: 'mamaFuel.moss.50',
                  borderColor: 'mamaFuel.moss.300'
                }}
              >
                Contact Us
              </Link>
            </Stack>
          </Flex>
        </Flex>

        {/* Social Media Icons and Create Button */}
        <HStack spacing="3" align="center">
          {/* Social Media Icons */}
          <HStack spacing="2" display={{ base: 'none', md: 'flex' }}>
            <IconButton
              aria-label="Follow us on Instagram"
              icon={FaInstagram && <Icon as={FaInstagram as any} boxSize={7} />}
              size="md"
              variant="ghost"
              color="mamaFuel.moss.700"
              _hover={{
                color: '#E4405F',
                bg: 'mamaFuel.moss.50'
              }}
              onClick={() => handleSocialClick('instagram')}
            />
            <IconButton
              aria-label="Follow us on Facebook"
              icon={FaFacebook && <Icon as={FaFacebook as any} boxSize={7} />}
              size="md"
              variant="ghost"
              color="mamaFuel.moss.700"
              _hover={{
                color: '#1877F2',
                bg: 'mamaFuel.moss.50'
              }}
              onClick={() => handleSocialClick('facebook')}
            />
          </HStack>

          {/* Create Button */}
          {location.pathname !== '/register' && (
            <Button
              as={RouterLink}
              to="/register"
              fontSize="sm"
              fontWeight="600"
              variant="primary"
              display={{ base: 'none', lg: 'block' }}
              alignItems="center"
              justifyContent="center"
              lineHeight="9"
            >
              Create Your MommaFuel
            </Button>
          )}

          {/* Mobile Navigation */}
          <Box display={{ base: 'block', lg: 'none' }} ml="auto" mr="0">
            <Menu>
              <MenuButton 
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant="ghost"
                color="mamaFuel.moss.700"
                fontWeight="600"
                ref={mobileMenuRef}
              >
                {getCurrentPageName()}
              </MenuButton>
              <MenuList zIndex="1001">
                <MenuItem as={RouterLink} to="/">
                  Home
                </MenuItem>
                <MenuItem as={RouterLink} to="/how-it-works">
                  How It Works
                </MenuItem>
                <MenuItem as={RouterLink} to="/faq">
                  FAQ
                </MenuItem>
                <MenuItem as={RouterLink} to="/register">
                  Create Your MommaFuel
                </MenuItem>
                <MenuItem as={RouterLink} to="/find-a-friend">
                  Find a Friend's MommaFuel
                </MenuItem>
                <MenuItem as={RouterLink} to="/login">
                  Login to My MommaFuel
                </MenuItem>
                <MenuItem as={RouterLink} to="/company-access">
                  Company Access
                </MenuItem>
                <MenuItem as={RouterLink} to="/support">
                  Contact Us
                </MenuItem>
                <MenuItem as={RouterLink} to="https://www.facebook.com/people/MommaFuel/61577361631105/">
                  Facebook
                </MenuItem>
                <MenuItem as={RouterLink} to="https://www.instagram.com/mommafuel_/">
                  Instagram
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>
        </HStack>

        {/* Mobile MommaFuel Finder Popover */}
        <Popover
          isOpen={isMobilePopoverOpen}
          onClose={onMobilePopoverClose}
          placement="bottom"
          closeOnBlur={true}
        >
          <PopoverContent p={2} width="auto" minWidth={{ base: "90vw", lg: "500px" }} display={{ lg: 'none' }}>
            <PopoverArrow />
            <PopoverBody>
              <InputGroup size="md">
                <Input
                  ref={mobileInputRef}
                  placeholder="Enter MommaFuel ID"
                  value={registryId}
                  onChange={(e) => setRegistryId(e.target.value)}
                  onKeyDown={handleKeyPress}
                  pr="4.5rem"
                  bg="white"
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" onClick={handleViewRegistry}>
                    Go
                  </Button>
                </InputRightElement>
              </InputGroup>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </Flex>

      
      {/* Spacer to prevent content from being hidden under the fixed navbar */}
      <Box height="60px" width="100%" />
    </Box>
  );
};

export default Navigation;
